import React, { useState, useRef } from "react";
import { Button, Typography, Box, CircularProgress } from "@mui/material";
import ReactMarkdown from "react-markdown";
import UploadTab from './UploadTab';
import UploadDocumentsTab from './UploadDocumentsTab';

import AudioRecorderTab from './Audiorecordertab';
import DictateTab from './Dictatetab';

import { generateAudioTimestamp, triggerUploadCompletion } from './Audiorecordertab';
import axios from "axios";
import "./config/axiosConfig";
import { useSiteSettings } from './context/SiteSettingsContext';
import { useAppMode } from './App';
import { useAuth } from "./hooks/useAuth";

type ToastHandlers = {
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarSeverity: React.Dispatch<React.SetStateAction<"success" | "warning" | "error">>;
  setOpenSnackbar: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarSticky: React.Dispatch<React.SetStateAction<boolean>>;
};

const SessionGenerators: React.FC<ToastHandlers> = ({
  setSnackbarMessage,
  setSnackbarSeverity,
  setOpenSnackbar,
  setSnackbarSticky,
}) => {
  const mode = useAppMode();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isUpLoading, setIsUpLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const sessionIdRef = useRef<string>(''); // Ref für session_id
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const {
    inlineAnnouncement,
    closeInlineAnnouncement,
    dismissInlineAnnouncement,
    trackInlineAnnouncementShown,
    trackInlineAnnouncementClick,
  } = useAuth();

  React.useEffect(() => {
    if (inlineAnnouncement) void trackInlineAnnouncementShown();
  }, [inlineAnnouncement?.id]);

  const showErrorToast = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarSticky(true);
    setOpenSnackbar(true);
  };

  // Upload eines Audio-Files
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      alert('Keine Audiodatei ausgewählt.');
      return;
    }
    sessionIdRef.current = generateAudioTimestamp();

    // Extrahiere ursprünglichen Namen und Endung
    const originalName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name; // Ohne Endung
    const extension = file.name.substring(file.name.lastIndexOf('.'));
    const timestampFilename = generateAudioTimestamp(extension); // Timestamp + Extension generieren

    const chunkSize = 1024 * 1024 * 2; // 10MB pro Chunk
    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedChunks = 0;

    setIsUpLoading(true); // Fortschrittsanzeige starten

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * chunkSize, (i + 1) * chunkSize);
      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('filename', timestampFilename);
      formData.append('originalName', originalName);
      formData.append('chunkIndex', String(i));
      formData.append('totalChunks', String(totalChunks));

      try {
        await axios.post('/api/services/upload-audio/', formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(((uploadedChunks + 1) / totalChunks) * 100);
            setUploadProgress(percentCompleted);
          }
        });

        uploadedChunks++;
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Hochladen:', error);
        showErrorToast("Das Hochladen ist fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.");
        setIsUpLoading(false);
        return;
      }
    }
    setIsUpLoading(false);

    // 🔹 **WICHTIG: `triggerUploadCompletion` am Ende aufrufen**
    try {
      await triggerUploadCompletion(sessionIdRef.current, timestampFilename, originalName, 0);
      //alert(`Datei "${originalName}" erfolgreich hochgeladen und verarbeitet.`);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim automatischen Abschluss der Verarbeitung:', error);
      alert('Fehler beim automatischen Abschluss der Verarbeitung. Bitte versuchen Sie es manuell.');
    }

    setUploadProgress(100);
    setIsLoading(false);
  };

  // Nur die Tab-Sichtbarkeit hängt vom Mode ab (ganze Tab-Blöcke ein/ausblenden)
  // mode=session: AudioRecorderTab + DictateTab + UploadTab
  // mode=case: DictateTab + UploadDocumentsTab
  const tabs = [
    ...(mode === 'session'
      ? [
          {
            id: "record",
            title: "Aufnehmen",
            icon: (
              <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 11.7246V20.3047" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.5 8.86523V23.1453" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.5 6.00586V26.0059" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.5 8.86523V23.1453" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M28.5 11.7246V20.3047" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ),
            content: <AudioRecorderTab />,
          },
        ]
      : []),

    {
      id: "dictate",
      title: "Diktieren",
      icon: (
        <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.513 20.6641C19.4597 20.6641 21.8464 18.2774 21.8464 15.3307V7.9974C21.8464 5.05073 19.4597 2.66406 16.513 2.66406C13.5664 2.66406 11.1797 5.05073 11.1797 7.9974V15.3307C11.1797 18.2774 13.5664 20.6641 16.513 20.6641Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6.32031 12.8672V15.1339C6.32031 20.7605 10.8936 25.3339 16.5203 25.3339C22.147 25.3339 26.7203 20.7605 26.7203 15.1339V12.8672" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14.6641 8.57219C15.8641 8.13219 17.1707 8.13219 18.3707 8.57219" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15.4531 11.3978C16.1598 11.2111 16.8931 11.2111 17.5998 11.3978" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16.5156 25.3359V29.3359" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      content: (
        <DictateTab mode={mode} />
      )
    },

    ...(mode === 'session'
      ? [
          {
            id: "upload",
            title: "Hochladen",
            icon: (
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12.5 15.5V9.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.5 11.5L12.5 8.5L15.5 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ),
            content: <UploadTab onUploadError={showErrorToast} />,
          },
        ]
      : []),

    ...(mode === 'case'
      ? [
          {
            id: "documents",
            title: "Dokumenten hochladen",
            icon: (
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.5 3.5H14.5L18.5 7.5V20.5H7.5V3.5Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
                <path d="M14.5 3.5V7.5H18.5" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />

                <path d="M12.5 16.5V11" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                <path d="M10.5 13L12.5 11L14.5 13" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ),
            content: (
              <UploadDocumentsTab onUploadError={showErrorToast} />
            )
          },
        ]
      : []),

    /*{
      id: "describe",
      title: "Beschreiben",
      icon: (
        <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.88665 26.0352C7.07332 26.0352 6.31332 25.7552 5.76665 25.2352C5.07332 24.5819 4.73998 23.5952 4.85998 22.5285L5.35332 18.2085C5.44665 17.3952 5.93998 16.3152 6.51332 15.7285L17.46 4.14186C20.1933 1.24853 23.0466 1.16853 25.94 3.90186C28.8333 6.6352 28.9133 9.48853 26.18 12.3819L15.2333 23.9685C14.6733 24.5685 13.6333 25.1285 12.82 25.2619L8.52665 25.9952C8.29998 26.0085 8.09998 26.0352 7.88665 26.0352Z" fill="currentColor" />
          <path d="M23.6199 14.6083C23.5933 14.6083 23.5533 14.6083 23.5266 14.6083C19.3666 14.1949 16.0199 11.0349 15.3799 6.9016C15.2999 6.35493 15.6733 5.84826 16.2199 5.75493C16.7666 5.67493 17.2733 6.04826 17.3666 6.59493C17.8733 9.8216 20.4866 12.3016 23.7399 12.6216C24.2866 12.6749 24.6866 13.1683 24.6333 13.7149C24.5666 14.2216 24.1266 14.6083 23.6199 14.6083Z" fill="currentColor" />
          <path d="M28.5 30.3418H4.5C3.95333 30.3418 3.5 29.8885 3.5 29.3418C3.5 28.7951 3.95333 28.3418 4.5 28.3418H28.5C29.0467 28.3418 29.5 28.7951 29.5 29.3418C29.5 29.8885 29.0467 30.3418 28.5 30.3418Z" fill="currentColor" />
        </svg>
      ),
      content: (
        // <DescribeTab />
        <Typography variant="h6"><Box
        sx={{
          padding: 3,
          backgroundColor: "rgba(64, 147, 122, 0.41)",
          border: "1px solid #32768d",
          borderRadius: "8px",
          textAlign: "center"
        }}
      ><Typography variant="h6" gutterBottom>🚧 In Arbeit</Typography>
        <Typography variant="body1">Diese Funktion wird bald verfügbar sein. Wir arbeiten daran!</Typography>
      </Box></Typography>
      )
    }*/
  ];

  // Falls du von session -> case wechselst, kann activeTab out-of-range werden
  const safeActiveTab = Math.min(activeTab, Math.max(0, tabs.length - 1));

  const handleInlineNavigate = (url: string) => {
    if (url === "/start-tour") {
      window.dispatchEvent(new CustomEvent("start-help-tour"));
      return;
    }
    if (url.startsWith("/")) {
      window.location.assign(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="lg:mb-0 lg:w-[701px]">
     {/* <div
        style={{
          marginBottom: '8px',
          padding: '6px 10px',
          fontSize: '12px',
          borderRadius: '4px',
          backgroundColor: '#e0f2fe',
          color: '#111827',
        }}
      >
        SessionGenerators – Mode {mode}
      </div>*/}
      <div className="lg:w-mb-8">

        <div id="dashboard-layout-left" className="">
          <div className="upload-container tw-relative lg:tw-z-[102]">
            <div data-testid="upload-wizard" className="_uploadWizardCard_1aueu_1 tw-relative">
              <div id="session-generator" className="sc-fqkvVR jiKqPF">
                <div dir="ltr" data-orientation="horizontal" style={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100%" }}>
                  <div role="tablist" aria-orientation="horizontal" className="_tabList_yedxm_1 tab-list" style={{ outline: 'none', marginBottom: '10px' }} tabIndex={0}>
                    {tabs.map((tab, index) => (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={safeActiveTab === index}
                        aria-controls={`tab-content-${tab.id}`}
                        data-state={safeActiveTab === index ? "active" : "inactive"}
                        id={`tab-trigger-${tab.id}`}
                        key={tab.id}
                        className="_tabButton_yedxm_16 tab-btn"
                        tabIndex={-1}
                        onClick={() => setActiveTab(index)}
                      >
                        {tab.icon}
                        <span
                          className={`tab-btn_label ml-1${
                            safeActiveTab !== index ? "hidden lg:inline ml-1" : ""
                          }`}
                        >
                          {tab.title}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div id={`tab-content-${tabs[safeActiveTab].id}`} style={{ padding: "20px", backgroundColor: "#FFFFFF", border: "1px solid #C5E1A5", borderRadius: "8px", minHeight: "200px" }}>
                    {tabs[safeActiveTab].content}
                  </div>
                </div>
              </div>
            </div>

            {inlineAnnouncement && (
              <div
                style={{
                  marginTop: "14px",
                  padding: "22px 24px",
                  background: "linear-gradient(120deg, rgba(50,118,141,0.08) 0%, rgba(247,251,253,0.95) 42%, rgba(231,165,85,0.14) 100%)",
                  border: "1px solid rgba(50, 118, 141, 0.14)",
                  borderLeft: "5px solid #32768d",
                  borderRadius: "14px",
                  boxShadow: "0 10px 28px rgba(50, 118, 141, 0.08)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div style={{ display: "flex", gap: "24px", alignItems: "center", paddingRight: "52px" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {inlineAnnouncement.title && (
                      <div
                        style={{
                          marginBottom: "14px",
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          lineHeight: 1.25,
                          color: "#17303b",
                        }}
                      >
                        {inlineAnnouncement.title}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                      {inlineAnnouncement.image && (
                        <div
                          style={{
                            flexShrink: 0,
                            width: "132px",
                            minHeight: "132px",
                            borderRadius: "12px",
                            background: "transparent",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            padding: "0",
                            transform: "translateY(6px)",
                          }}
                        >
                          <img
                            src={inlineAnnouncement.image}
                            alt=""
                            style={{
                              width: "148px",
                              maxHeight: "148px",
                              objectFit: "contain",
                              display: "block",
                              background: "transparent",
                            }}
                          />
                        </div>
                      )}
                      <div style={{ minWidth: 0, flex: 1, paddingTop: "2px", maxWidth: "880px" }}>
                        {inlineAnnouncement.body && (
                          <div
                            className="prose max-w-none text-sm leading-6"
                            style={{ color: "#36505c", maxWidth: "820px", marginTop: 0 }}
                          >
                            <ReactMarkdown
                              components={{
                                p: ({ children }) => (
                                  <p style={{ margin: "0 0 10px 0" }}>{children}</p>
                                ),
                                ul: ({ children }) => (
                                  <ul
                                    style={{
                                      margin: "10px 0 12px 0",
                                      paddingLeft: "20px",
                                      listStyleType: "disc",
                                    }}
                                  >
                                    {children}
                                  </ul>
                                ),
                                li: ({ children }) => (
                                  <li
                                    style={{
                                      margin: "0 0 6px 0",
                                      padding: 0,
                                      background: "transparent",
                                      boxShadow: "none",
                                      border: 0,
                                      listStyleType: "disc",
                                    }}
                                  >
                                    {children}
                                  </li>
                                ),
                                a: ({ href, children }) => (
                                  <a
                                    href={href}
                                    target={href && /^https?:\/\//.test(href) ? "_blank" : undefined}
                                    rel={href && /^https?:\/\//.test(href) ? "noopener noreferrer" : undefined}
                                    style={{
                                      color: "#32768d",
                                      textDecoration: "underline",
                                      textUnderlineOffset: "2px",
                                    }}
                                  >
                                    {children}
                                  </a>
                                ),
                                strong: ({ children }) => (
                                  <strong style={{ color: "#17303b", fontWeight: 700 }}>
                                    {children}
                                  </strong>
                                ),
                              }}
                            >
                              {inlineAnnouncement.body}
                            </ReactMarkdown>
                          </div>
                        )}
                        {(inlineAnnouncement.cta1_label || inlineAnnouncement.cta2_label || inlineAnnouncement.allow_dismiss !== false) && (
                          <div style={{ marginTop: "16px", display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center", justifyContent: "flex-start" }}>
                            {inlineAnnouncement.cta1_label && inlineAnnouncement.cta1_url && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await trackInlineAnnouncementClick(1);
                                  handleInlineNavigate(inlineAnnouncement.cta1_url as string);
                                }}
                                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                                style={{ paddingTop: "6px", paddingBottom: "6px", minHeight: "unset" }}
                              >
                                {inlineAnnouncement.cta1_label}
                              </button>
                            )}
                            {inlineAnnouncement.cta2_label && inlineAnnouncement.cta2_url && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await trackInlineAnnouncementClick(2);
                                  handleInlineNavigate(inlineAnnouncement.cta2_url as string);
                                }}
                                style={{
                                  border: 0,
                                  background: "transparent",
                                  padding: 0,
                                  color: "#32768d",
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                }}
                              >
                                {inlineAnnouncement.cta2_label}
                              </button>
                            )}
                            {inlineAnnouncement.allow_dismiss !== false && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await dismissInlineAnnouncement();
                                }}
                                style={{
                                  border: 0,
                                  background: "transparent",
                                  padding: 0,
                                  fontSize: "0.9rem",
                                  color: "#32768d",
                                  cursor: "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                Nicht mehr anzeigen
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {inlineAnnouncement.allow_close !== false && (
                    <button
                      type="button"
                      aria-label="Announcement schließen"
                      onClick={async () => {
                        await closeInlineAnnouncement();
                      }}
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        width: "32px",
                        height: "32px",
                        border: 0,
                        background: "rgba(255,255,255,0.72)",
                        padding: 0,
                        lineHeight: 1,
                        fontSize: "20px",
                        color: "#64748b",
                        cursor: "pointer",
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            )}

            {isUpLoading && (
              <div style={{ marginTop: '10px' }}>
                <Typography variant="body1">Upload läuft... {uploadProgress}%</Typography>
                <CircularProgress variant="determinate" value={uploadProgress} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionGenerators;
